/**
 * Generates a full UI color palette from two input colors.
 *
 * @param {string} primary - Brand color hex (e.g. "#3E4E5B")
 * @param {string} accent  - Highlight/action color hex (e.g. "#FFC300")
 * @returns {object} Full theme object with derived shades
 */
export default function buildTheme(primary = "#3E4E5B", accent = "#FFC300") {
  const p = parseHex(primary);
  const a = parseHex(accent);

  return {
    primary,
    primaryDark: hslShift(p, 0, 0.02, -0.07),
    darkBg: hslShift(p, 0, 0.08, -0.16),
    cardBg: hslShift(p, 0, 0.06, -0.13),
    borderDark: hslShift(p, 0, 0.01, -0.01),
    accent,
    textWhite: "#fff",
    textLight: "#f0f0f0",
    textOnPrimary: contrastText(p),
    textOnAccent: contrastText(a),
  };
}

/** Parse "#RRGGBB" to { r, g, b } (0-255) */
function parseHex(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

/** Convert { r, g, b } to "#RRGGBB" */
function toHex({ r, g, b }) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** RGB to HSL (h: 0-360, s: 0-1, l: 0-1) */
function rgbToHsl({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

/** HSL to RGB */
function hslToRgb({ h, s, l }) {
  const hue = ((h % 360) + 360) % 360;
  if (s === 0) { const v = Math.round(l * 255); return { r: v, g: v, b: v }; }
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let rn, gn, bn;
  if (hue < 60) { rn = c; gn = x; bn = 0; }
  else if (hue < 120) { rn = x; gn = c; bn = 0; }
  else if (hue < 180) { rn = 0; gn = c; bn = x; }
  else if (hue < 240) { rn = 0; gn = x; bn = c; }
  else if (hue < 300) { rn = x; gn = 0; bn = c; }
  else { rn = c; gn = 0; bn = x; }
  return {
    r: Math.round((rn + m) * 255),
    g: Math.round((gn + m) * 255),
    b: Math.round((bn + m) * 255),
  };
}

/** Shift an RGB color by HSL deltas and return hex */
function hslShift(rgb, dh, ds, dl) {
  const hsl = rgbToHsl(rgb);
  hsl.h += dh;
  hsl.s = Math.max(0, Math.min(1, hsl.s + ds));
  hsl.l = Math.max(0, Math.min(1, hsl.l + dl));
  return toHex(hslToRgb(hsl));
}

/** Return "#fff" or "#2E3C47" based on luminance contrast */
function contrastText(rgb) {
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum > 0.5 ? "#2E3C47" : "#fff";
}
